/**
 * Unified Filter Configuration
 * 
 * Costanti unificate per filtri km usati in:
 * - Dashboard (Barche e Motori)
 * - Map (BoatMap)
 * - RadiusFilter
 * 
 * IMPORTANTE: Tutti i componenti DEVONO usare questi stessi range
 */

// Coordinate admin - now loaded from API via geoConfig
// Use getAdminCoords() from './geoConfig' instead
export const ADMIN_LECCE_COORDS = {
  lat: 40.3516,
  lng: 18.1723,
  name: 'Lecce (Admin)'
};

// Range distanza UNIFICATI per tutti i componenti
export const UNIFIED_DISTANCE_RANGES = [
  { label: '10km', value: 10, max: 10 },    // Vicinissimo
  { label: '25km', value: 25, max: 25 },    // Vicino
  { label: '50km', value: 50, max: 50 },    // Zona
  { label: '100km', value: 100, max: 100 }, // Provincia
  { label: '200km', value: 200, max: 200 }, // Regione
  { label: '500km', value: 500, max: 500 }, // Area
  { label: '1000km', value: 1000, max: 1000 }, // Nazione
  { label: 'Tutte', value: -1, max: Infinity } // Tutte
];

// Array semplice di valori per uso diretto (esclude -1 per compatibilità)
export const UNIFIED_RADIUS_VALUES = [10, 25, 50, 100, 200, 500, 1000, -1] as const;

// Tipo per i valori radius
export type RadiusValue = 10 | 25 | 50 | 100 | 200 | 500 | 1000 | -1;

// Colori per fascia distanza - usati per badge e marker
// Usa string come chiave per evitare problemi con -1
export const COLORS_BY_DISTANCE: Record<string, string> = {
  '10': '#f97316',   // arancione - vicinissimo
  '25': '#eab308',   // giallo - vicino
  '50': '#22c55e',   // verde - zona
  '100': '#3b82f6',  // blu - provincia
  '200': '#8b5cf6',  // viola - regione
  '500': '#ec4899',  // rosa - area
  '1000': '#14b8a6', // teal - nazione
  '-1': '#6b7280'    // grigio (tutte)
};

// Colori CSS Tailwind per sfondo badge
export const TAILWIND_COLORS_BY_DISTANCE: Record<string, string> = {
  '10': 'bg-orange-500',
  '25': 'bg-yellow-500',
  '50': 'bg-green-500',
  '100': 'bg-blue-500',
  '200': 'bg-purple-500',
  '500': 'bg-pink-500',
  '1000': 'bg-teal-500',
  '-1': 'bg-gray-500'
};

// Funzione per ottenere il colore dalla distanza
export function getColorForDistance(distance: number): string {
  if (distance <= 10) return COLORS_BY_DISTANCE['10'];
  if (distance <= 25) return COLORS_BY_DISTANCE['25'];
  if (distance <= 50) return COLORS_BY_DISTANCE['50'];
  if (distance <= 100) return COLORS_BY_DISTANCE['100'];
  if (distance <= 200) return COLORS_BY_DISTANCE['200'];
  if (distance <= 500) return COLORS_BY_DISTANCE['500'];
  if (distance <= 1000) return COLORS_BY_DISTANCE['1000'];
  return COLORS_BY_DISTANCE['-1'];
}

// Alias for backward compatibility
export const getDistanceColor = getColorForDistance;

// Funzione per ottenere il colore Tailwind dalla distanza
export function getTailwindColorForDistance(distance: number): string {
  if (distance <= 10) return TAILWIND_COLORS_BY_DISTANCE['10'];
  if (distance <= 25) return TAILWIND_COLORS_BY_DISTANCE['25'];
  if (distance <= 50) return TAILWIND_COLORS_BY_DISTANCE['50'];
  if (distance <= 100) return TAILWIND_COLORS_BY_DISTANCE['100'];
  if (distance <= 200) return TAILWIND_COLORS_BY_DISTANCE['200'];
  if (distance <= 500) return TAILWIND_COLORS_BY_DISTANCE['500'];
  if (distance <= 1000) return TAILWIND_COLORS_BY_DISTANCE['1000'];
  return TAILWIND_COLORS_BY_DISTANCE['-1'];
}

// Funzione per ottenere la fascia dalla distanza
export function getDistanceRange(distance: number): number {
  if (distance <= 10) return 10;
  if (distance <= 25) return 25;
  if (distance <= 50) return 50;
  if (distance <= 100) return 100;
  if (distance <= 200) return 200;
  if (distance <= 500) return 500;
  if (distance <= 1000) return 1000;
  return -1;
}

// Funzione per ottenere l'indice del range precedente
export function getPreviousRadius(radius: number): number {
  const idx = UNIFIED_RADIUS_VALUES.indexOf(radius as RadiusValue);
  if (idx <= 0) return -Infinity;
  return UNIFIED_RADIUS_VALUES[idx - 1] as number;
}

// Funzione per verificare se un valore è un range valido
export function isValidRadius(radius: number): boolean {
  return (UNIFIED_RADIUS_VALUES as readonly number[]).includes(radius);
}

// Tipo per item type nei filtri
export type FilterItemType = 'boats' | 'motors' | 'all';

// Esporta la costante per backward compatibility
export const DISTANCE_RANGES = [10, 25, 50, 100, 200, 500, 1000];
export const LECCE_COORDS = ADMIN_LECCE_COORDS;
